import React from "react";
import { Link } from "react-router-dom";
import ProductCard from "../ui/ProductCard";
import { TooltipProvider } from "../ui/tooltip";

const NewArrivalsSection = ({ title, subtitle, products, isLoading }) => {
  // Product Skeleton Loader Component
  const ProductSkeletonLoader = () => {
    return (
      <div className="bg-white rounded-mobile-lg shadow-mobile overflow-hidden">
        <div className="aspect-square w-full bg-gray-200 animate-pulse"></div>
        <div className="p-3 xs:p-4">
          <div className="h-4 bg-gray-200 animate-pulse rounded-full mb-2 w-3/4"></div>
          <div className="h-3 bg-gray-200 animate-pulse rounded-full mb-3 w-1/2"></div>
          <div className="h-5 bg-gray-200 animate-pulse rounded-full mb-3 w-1/3"></div>
          <div className="h-8 bg-gray-200 animate-pulse rounded-full w-full"></div>
        </div>
      </div>
    );
  };
  
  if (!isLoading && (!products || products.length === 0)) return null;

  return (
    <section className="bg-gray-50 py-4 sm:py-6 md:py-8">
      <div className="w-full md:container mx-auto px-2 sm:px-4">
        <div className="mb-4 md:mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl xs:text-2xl sm:text-3xl font-heading font-bold text-admin-slate-800 mb-2">
              {title}
            </h2>
            <p className="text-sm xs:text-base text-admin-slate-500">
              {subtitle}
            </p>
          </div>
          <Link
            to="/products"
            className="flex-shrink-0 text-sm md:text-base font-bold text-[#1E3A8A] hover:text-blue-800 flex items-center transition-colors group"
          >
            View All <span className="ml-1 transition-transform group-hover:translate-x-1">›</span>
          </Link>
        </div>

        {/* Products row - horizontal scroll on mobile/tablet, single grid row from lg up.
            Same pattern as the Featured Categories rows, so this is always one row,
            not just capped-but-still-wrapping on smaller screens. */}
        <TooltipProvider>
          <div className="flex overflow-x-auto lg:grid lg:grid-cols-6 gap-3 md:gap-6 snap-x snap-mandatory pb-4 -mx-2 px-2 lg:mx-0 lg:px-0 lg:overflow-visible scrollbar-hide">
            {isLoading ? (
              // Show skeleton loaders when loading
              Array(6).fill(0).map((_, index) => (
                <div key={`skeleton-${index}`} className="w-[45%] sm:w-[30%] lg:w-auto flex-shrink-0 snap-start">
                  <ProductSkeletonLoader />
                </div>
              ))
            ) : products && products.length > 0 ? (
              // Show products when available, filter out any null/invalid products.
              // Capped to 6 to match the lg:grid-cols-6 layout - one row only, rest via "View all"
              products.filter(product => product && (product._id || product.id)).slice(0, 6).map((product) => (
                <div key={product._id || product.id} className="w-[45%] sm:w-[30%] lg:w-auto flex-shrink-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))
            ) : (
              // Show message when no products
              <div className="w-full py-10 text-center">
                <p className="text-admin-slate-500">No new arrivals available at this time.</p>
              </div>
            )}
          </div>
        </TooltipProvider>
      </div>
    </section>
  );
};

export default NewArrivalsSection;
